import type { JSX } from 'react';
import type { ReactNode } from 'react';

export interface RingProps {
  /** Процент 0–100 (обрезается). */
  value: number;
  /** Диаметр, px. */
  size?: number;
  /** Толщина кольца, px. */
  stroke?: number;
  /** Цвет дуги (по умолчанию оранжевый; 100 % — зелёный, если `doneColor`). */
  color?: string;
  /** Цвет при 100 % (по умолчанию — тот же `color`). */
  doneColor?: string;
  /** Текст в центре; по умолчанию «NN%». null — пусто. */
  label?: ReactNode;
  title?: string;
}

/**
 * Кольцо прогресса (курс, траектория, точка): SVG без библиотек, цвета —
 * токены (тёмная тема работает). Подпись в центре масштабируется от size.
 */
export function Ring({ value, size = 44, stroke = 4, color = 'var(--fk-orange)', doneColor, label, title }: RingProps): JSX.Element {
  const v = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const arc = v >= 100 && doneColor ? doneColor : color;
  return (
    <span
      role="img"
      aria-label={title ?? `${Math.round(v)}%`}
      title={title}
      style={{ position: 'relative', width: size, height: size, display: 'inline-grid', placeItems: 'center', flexShrink: 0 }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} style={{ stroke: 'var(--fk-border)' }} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - v / 100)}
          style={{ stroke: arc, transition: 'stroke-dashoffset var(--fk-dur) var(--fk-ease)' }}
        />
      </svg>
      {label !== null ? (
        <span style={{ position: 'relative', fontSize: Math.max(10, Math.round(size * 0.26)), fontWeight: 700, color: 'var(--fk-text)', fontVariantNumeric: 'tabular-nums' }}>
          {label ?? `${Math.round(v)}%`}
        </span>
      ) : null}
    </span>
  );
}
